import React, { useEffect, useRef, useState, useCallback } from 'react';
import copyIcon from '../../assets/image/copy_icon.svg';
import defaultIcon from '../../assets/image/company_default_icon.png';
import './dashboardstyle.scss';
import arrowDownIcon from '../../assets/image/arrow_down_icon.svg';
import clickGrayIcon from '../../assets/image/click_gray.svg';
import clickBlueIcon from '../../assets/image/click_blue.svg';
import plusIcon from '../../assets/image/plus_icon.svg';
import arrowDownBlackIcon from '../../assets/image/arrow_down_dark.svg';
import { Package } from 'lucide-react';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useDispatch, useSelector } from 'react-redux';
import NavigationDashboardTab from './navigationDashboardTab';
import Button from '../../component/ui/Button';
import InputField from '../../component/form/FormInput';
import Modal from '../../component/modal/Modal';
import AddCardModel from './components/addCardModel';
import CustomDateRange from './components/customDateRange';
import {
	createNewDashboardLayout,
	fetchDashboardCards,
	fetchDashboardLayout,
	setDefaultLayout,
	updateLayout,
} from '../../services/dashboard';
import { dateFilterOptions } from './data';
import { WidthProvider } from 'react-grid-layout';
import PageLoader from '../../component/PageLoader';
import TableCard from './components/table_card';
import RecentTransactions from './components/recent_transactions';
import SellingProducts from './components/selling_products';
import SummaryCard from './components/summary_card';
import InventorySummary from './components/inventory_summary';
import { toast } from 'react-toastify';
import GetStartedScreen from './get_started';
import NotificationPage from './notification';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchUserInfo } from '../../store/action/userInfo';
import { hasPermission } from '../../utils/controller';
import PricingSection from '../subscriptionPlan/components/PricingSection';
import { fetchSubscriptionPlans } from '../../services/subscription';
import { Responsive } from 'react-grid-layout';
const ResponsiveGridLayout = WidthProvider(Responsive);

const Dashboard = () => {
	const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
	const [plans, setPlans] = useState([]);
	const [loading, setLoading] = useState(false);
	const [billing, setBilling] = useState('monthly');
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState('Dashboard');
	const [globalFilter, setGlobalFilter] = useState({
		label: 'Today',
		filter_by: 'today',
		start_date: null,
		end_date: null,
	});
	const [cardName, setCardName] = useState('');
	const [totalPercentage, setTotalPercentage] = useState('');
	const [isOpenModal, setIsOpenModal] = useState(false);
	const [isAddCardModal, setIsAddCardModal] = useState(false);
	const [showDateOptions, setShowDateOptions] = useState(false);
	const [showOptions, setShowOptions] = useState(false);
	const userDetails = useSelector((state) => state?.userInfo?.userInfo);
	const dispatch = useDispatch();
	const [layoutSettings, setLayoutSettings] = useState({
		isDraggable: false,
		isResizable: false,
		isCustomizing: false,
		card_list: [],
		layout_list: [],
		active_cards: [],
		active_layout: {},
		customized_layout: {},
	});

	const [activeViewId, setActiveViewId] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const latestLayoutRef = useRef([]);
	const layoutBackupRef = useRef([]);
	const location = useLocation();
	const fetchUserLayoutConfig = async () => {
		setIsLoading(true);
		try {
			const { layouts } = await fetchDashboardLayout();
			const card_res = await fetchDashboardCards();

			let active_layout = layouts.find((layout) => layout.is_active);

			if (!active_layout) {
				const default_layout = layouts.find((layout) => layout.is_default);
				active_layout = {
					...default_layout,
					is_active: true,
				};
			}

			const active_layout_card_ids = active_layout.layout.map((layout) => layout.i);

			const card_list = card_res.cards.map((card) => {
				if (active_layout_card_ids.includes(card.layout_card_id)) {
					return {
						...card,
						is_checked: true,
					};
				}

				return {
					...card,
					is_checked: false,
				};
			});

			setLayoutSettings((prev) => ({
				...prev,
				active_layout: active_layout,
				layout_list: layouts,
				card_list: card_list,
			}));
		} catch (err) {
			console.error('Fetch layout failed:', err);
		} finally {
			setIsLoading(false);
		}
	};

	const updateView = (activeLayout) => {
		setLayoutSettings((prev) => ({
			...prev,
			active_layout: activeLayout,
		}));
		latestLayoutRef.current = activeLayout;
	};

	const handleLayoutChange = useCallback(
		(layout) => {
			if (layoutSettings.isCustomizing) {
				latestLayoutRef.current = layout;
				setLayoutSettings((prev) => ({
					...prev,
					active_layout: {
						...prev.active_layout,
						layout: layout,
					},
				}));
			}
		},
		[layoutSettings.isCustomizing],
	);

	const handleViewSwitch = (viewId) => {
		if (layoutSettings.isCustomizing) return;
		const view = layoutSettings.layout_list.find((v) => v.id === viewId);
		if (view) {
			setActiveViewId(viewId);
			updateView(view);
		}
	};

	const toggleCustomization = () => {
		if (!layoutSettings.isCustomizing) {
			layoutBackupRef.current = JSON.parse(JSON.stringify(layoutSettings.active_layout.layout));
			setLayoutSettings((prev) => ({
				...prev,
				isCustomizing: true,
				isDraggable: true,
				isResizable: false,
			}));
		} else {
			handleCancel();
		}
	};

	const handleSave = async () => {
		if (layoutSettings.active_layout.is_default && layoutSettings.active_layout.is_active) {
			setLayoutSettings((prev) => ({
				...prev,
				active_layout: {
					...prev.active_layout,
					layout: layoutBackupRef.current,
				},
				isCustomizing: false,
				isDraggable: false,
				isResizable: false,
			}));
			toast.info("Default layout can't customized or updated. So, we have layout changes reverted");
		} else {
			try {
				const { layout, globalFilters, is_active, name } = layoutSettings.active_layout;
				const data = {
					layout,
					globalFilters,
					is_active,
					name,
				};

				const res = await updateLayout(layoutSettings.active_layout.id, data);
				const newSaveLayout = res.layout;
				const layoutIndex = layoutSettings.layout_list.findIndex(
					(item) => item.id === newSaveLayout.id,
				);
				let layoutList = [...layoutSettings.layout_list];
				layoutList[layoutIndex] = newSaveLayout;
				setLayoutSettings((prev) => ({
					...prev,
					isCustomizing: false,
					layout_list: layoutList,
					active_layout: newSaveLayout,
				}));
				toast.success('Layout successfully saved!');
			} catch (err) {
				toast.error('Failed to save layout');
			}
		}
	};

	const handleCancel = () => {
		setLayoutSettings((prev) => ({
			...prev,
			active_layout: {
				...prev.active_layout,
				layout: layoutBackupRef.current,
			},
			isCustomizing: false,
			isDraggable: false,
			isResizable: false,
		}));
		toast.info('Changes reverted');
	};

	const handleRemoveCard = (id, e) => {
		e.preventDefault();
		const newLayout = layoutSettings.active_layout.layout.filter((e) => e.i !== id);
		setLayoutSettings((prev) => ({
			...prev,
			active_layout: {
				...prev.active_layout,
				layout: newLayout,
			},
		}));
	};

	const handleCreateNewLayout = async () => {
		try {
			const layoutData = {
				name: cardName,
			};

			const { layout: newLayout } = await createNewDashboardLayout(layoutData);

			const updatedLayoutList = [...layoutSettings.layout_list, newLayout];

			setLayoutSettings((prev) => ({
				...prev,
				layout_list: updatedLayoutList,
				active_layout: newLayout,
			}));

			setCardName('');
			setIsOpenModal(false);
			toast.success('New Layout created successfully');
		} catch (e) {
			console.error(e);
			toast.error('Failed to create layout');
		}
	};

	const setDefaultLayoutView = async () => {
		try {
			const data = {
				id: Number(layoutSettings.active_layout.id),
			};

			const { layout } = await setDefaultLayout(data);
			const updated_layouts = layoutSettings.layout_list.map((l) => {
				if (l.id === layout.id) {
					return {
						...layout,
						is_active: true,
					};
				}
				return {
					...l,
					is_active: false,
				};
			});

			setLayoutSettings((prev) => ({
				...prev,
				layout_list: updated_layouts,
				active_layout: {
					...layout,
					is_active: true,
				},
			}));

			toast.success('Layout is set as default successfully');
		} catch (e) {
			toast.error(e.message || 'something went wrong!');
		}
	};

	const handleCopyText = (text) => {
		navigator.clipboard.writeText(text).then(() => {
			toast.success('Text copy successfully');
		});
	};

	const handleDataByDateRange = () => {};
	useEffect(() => {
		dispatch(fetchUserInfo());
	}, []);
	useEffect(() => {
		if (location && location?.state) {
			setActiveTab(location?.state.tab);
		} else {
			fetchUserLayoutConfig();
		}
	}, [location]);

	useEffect(() => {
		if (userDetails?.data) {
			const isSubscribed = userDetails?.data?.is_subscribed;
			const modalAlreadyShown = sessionStorage.getItem('subscription_modal_shown');
			if (!isSubscribed && !modalAlreadyShown) {
				if (!isSubscribed) {
					setShowSubscriptionModal(true);
					sessionStorage.setItem('subscription_modal_shown', 'true');
				}
			}
		}
	}, [userDetails]);

	const getPlans = async () => {
		try {
			setLoading(true);
			const response = await fetchSubscriptionPlans();
			setPlans(response.plans);
		} catch (error) {
			console.error(error);
			toast.error('Failed to fetch subscription plans');
			setPlans([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getPlans();
	}, []);

	if (isLoading) {
		return (
			<div
				className="d-flex justify-content-center align-items-center"
				style={{ height: '100%' }}
			>
				<span
					className="Page_loader"
					role="status"
					aria-hidden="true"
				></span>
			</div>
		);
	}

	return (
		<div className="dashboard-container">
			<div className="user-grid">
				<div className="user-details-grid">
					<img
						src={
							userDetails?.data?.organisation_logo !== '' &&
							userDetails?.data?.organisation_logo !== null &&
							userDetails?.data?.organisation_logo !== undefined
								? userDetails?.data?.organisation_logo
								: defaultIcon
						}
						width={65}
						height={65}
						className="rounded-4"
						alt="logo"
					/>
					<div className="p-2">
						<div className="user-name-text">{`${userDetails?.data?.organisation_name || ''}`}</div>
						<div className="user-id-row">
							<div>{`User Id: ${userDetails?.data?.user_id || ''}`}</div>
							<div style={{ margin: '0px 10px' }}>
								<img
									style={{ cursor: 'pointer' }}
									src={copyIcon}
									alt="copy"
									onClick={() => {
										handleCopyText(userDetails?.data?.user_id);
									}}
								/>
							</div>
							<div>{`Organization Id: ${userDetails?.data?.current_organisation_id || ''}`}</div>
							<div style={{ margin: '0px 10px' }}>
								<img
									style={{ cursor: 'pointer' }}
									src={copyIcon}
									alt="copy"
									onClick={() => {
										handleCopyText(userDetails?.data?.current_organisation_id);
									}}
								/>
							</div>
						</div>
					</div>
				</div>
				<div className="d-flex flex-row gap-4">
					{globalFilter.filter_by === 'custom_date' && activeTab === 'Dashboard' && (
						<CustomDateRange
							style={{ border: '1px solid #ccc' }}
							handleDataByDateRange={handleDataByDateRange}
							range={{ start_date: globalFilter.start_date, end_date: globalFilter.end_date }}
							setRange={setGlobalFilter}
						/>
					)}
					{activeTab === 'Dashboard' && (
						<div className="dropdown-container">
							<div
								className="select-box text-dark"
								style={{ backgroundColor: '#fff' }}
								onClick={() => setShowDateOptions(!showDateOptions)}
							>
								<div className="d-flex flex-row align-items-center gap-4">
									<div className="fs-5 font-semibold">{globalFilter.label}</div>
									<img
										src={arrowDownBlackIcon}
										alt="arrow"
										width={10}
										height={10}
									/>
								</div>
							</div>

							{showDateOptions && (
								<ul
									className="options-list"
									style={{ zIndex: 40 }}
								>
									{dateFilterOptions.map((option, idx) => (
										<li
											key={idx}
											onClick={() => {
												setGlobalFilter((prev) => ({
													...prev,
													filter_by: option.value,
													label: option.label,
												}));
												setShowDateOptions(false);
											}}
										>
											<div
												className={`${option.value === globalFilter.filter_by ? 'text-secondary' : 'text-dark'}`}
											>
												{option.label}
											</div>
										</li>
									))}
								</ul>
							)}
						</div>
					)}
				</div>
			</div>

			<div className="bg-header-color shadow-sm border-b border-gray-200">
				<div className="max-w-7xl mx-auto px-4 py-4">
					<div className="flex items-center justify-between h-16">
						<div className="d-flex justify-content-between align-content-center">
							<div>
								<div>
									<NavigationDashboardTab
										activeTab={activeTab}
										setActiveTab={setActiveTab}
										navTabs={['Dashboard', 'Getting Started', 'Notifications']}
									/>
								</div>
							</div>
							{activeTab === 'Dashboard' && (
								<div className="d-flex flex-row gap-2 space-x-2 items-center">
									{!layoutSettings.isCustomizing && !layoutSettings?.active_layout?.is_active && (
										<Button
											type="button"
											button="Set As Default"
											className="mt-0 btn-primary add-pro"
											onClick={() => setDefaultLayoutView()}
										/>
									)}
									{!layoutSettings.isCustomizing && (
										<div className="dropdown-container">
											<div
												className="select-box font-14"
												onClick={() => setShowOptions(!showOptions)}
											>
												<div className="px-2">{layoutSettings.active_layout.name}</div>
												<div className="mx-2">
													<img
														src={arrowDownIcon}
														alt="arrow"
													/>
												</div>
											</div>

											{showOptions && (
												<ul
													className="options-list"
													style={{ overflow: 'auto', maxHeight: '285px' }}
												>
													{layoutSettings.layout_list.map((option, idx) => (
														<li
															key={idx}
															className="d-flex justify-content-between align-items-center"
															onClick={() => {
																handleViewSwitch(parseInt(option.id));
																setShowOptions(false);
															}}
														>
															<div>{option.name}</div>
															<div className="mx-2">
																{option.id === layoutSettings.active_layout.id ? (
																	<img
																		src={clickBlueIcon}
																		alt=""
																	/>
																) : (
																	<img
																		src={clickGrayIcon}
																		alt=""
																	/>
																)}
															</div>
														</li>
													))}
													{hasPermission(
														'dashboard.manage_dashboard.view',
														userDetails?.data?.role?.role_permissions,
													) && (
														<li
															onClick={() => {
																setIsOpenModal(true);
																setShowOptions(false);
															}}
															className="d-flex justify-content-between align-items-center"
														>
															<div>{'Save New View'}</div>
															<div className="mx-2">
																<img
																	src={plusIcon}
																	alt=""
																/>
															</div>
														</li>
													)}
												</ul>
											)}
										</div>
									)}

									{layoutSettings.isCustomizing ? (
										<div className="d-flex flex-row gap-4">
											<Button
												type="button"
												disabled={
													!hasPermission(
														'dashboard.manage_dashboard.view',
														userDetails?.data?.role?.role_permissions,
													)
												}
												button="Add New Card"
												className="mt-0 btn-primary btn-bg-color add-pro"
												onClick={() => {
													const activeCardIds = new Set(
														layoutSettings.active_layout.layout.map((l) => l.i),
													);

													const updatedCardList = layoutSettings.card_list.map((card) => ({
														...card,
														is_checked: activeCardIds.has(card.layout_card_id),
													}));

													setLayoutSettings((prev) => ({
														...prev,
														card_list: updatedCardList,
													}));

													setIsAddCardModal(true);
												}}
											/>
											<Button
												type="button"
												button="Save"
												disabled={
													!hasPermission(
														'dashboard.manage_dashboard.view',
														userDetails?.data?.role?.role_permissions,
													)
												}
												className="mt-0 btn-primary btn-bg-color add-pro"
												onClick={handleSave}
											/>
											<Button
												type="button"
												close
												button=""
												className="mt-0 btn-danger add-pro"
												onClick={handleCancel}
											/>
										</div>
									) : (
										<Button
											type="button"
											button="Customize Dashboard"
											disabled={
												!hasPermission(
													'dashboard.manage_dashboard.view',
													userDetails?.data?.role?.role_permissions,
												)
											}
											className="mt-0 btn-primary btn-bg-color add-pro"
											onClick={toggleCustomization}
										/>
									)}
								</div>
							)}
							{activeTab === 'Getting Started' && totalPercentage !== '' && (
								<div className="d-none d-xl-block">
									<div
										className="fs-4 fw-normal"
										style={{ color: '#555B67' }}
									>
										Your quick setup guide
									</div>
									<div className="d-flex justify-content-center align-items-center gap-3">
										<div className="progress-bar">
											<div
												className="progress-fill"
												style={{ width: `${totalPercentage}%` }}
											></div>
										</div>
										<span className="progress-text">{totalPercentage}% completed</span>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Layout Content */}
			<div className=" mt-3 getting-start-ipad">
				{activeTab === 'Getting Started' && totalPercentage !== '' && (
					<div className="d-block d-xl-none">
						<div
							className="fs-4 fw-normal"
							style={{ color: '#555B67' }}
						>
							Your quick setup guide
						</div>
						<div className="d-flex justify-content-center align-items-center gap-3">
							<div className="progress-bar i-pad">
								<div
									className="progress-fill"
									style={{ width: `${totalPercentage}%` }}
								></div>
							</div>
							<span className="progress-text">{totalPercentage}% completed</span>
						</div>
					</div>
				)}
			</div>

			<div className="dashboard-body">
				{activeTab === 'Dashboard' ? (
					<div>
						{layoutSettings?.active_layout?.layout?.length === 0 ? (
							<div className="text-center py-12">
								<Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
								<h3 className="text-lg font-medium text-gray-900 mb-2">No widgets configured</h3>
								<p className="text-gray-600">This dashboard view doesn't have any widgets yet.</p>
							</div>
						) : (
							<ResponsiveGridLayout
								className="layout"
								compactType="vertical"
								layouts={{
									lg: layoutSettings?.active_layout?.layout,
									md: layoutSettings?.active_layout?.layout,
									sm: layoutSettings?.active_layout?.layout,
									xs: layoutSettings?.active_layout?.layout,
									xxs: layoutSettings?.active_layout?.layout,
								}}
								onLayoutChange={handleLayoutChange}
								breakpoints={{
									lg: 1200,
									md: 1025,
									sm: 0,
								}}
								cols={{
									lg: 10,
									md: 10,
									sm: 1,
								}}
								isDraggable={layoutSettings.isDraggable}
								isResizable={false}
								autoSize={true}
								// rowHeight={30}
								// preventCollision={false}
								margin={[15, 15]}
								onDragStop={(layout, oldItem, newItem, placeholder, e, element) => {}}
							>
								{layoutSettings.active_layout && layoutSettings.card_list ? (
									layoutSettings?.active_layout?.layout?.map((item) => {
										const card = layoutSettings?.card_list?.find(
											(c) => c.layout_card_id === item.i,
										);
										return (
											<div
												key={item.i}
												className={`metric-card-box`}
											>
												<DashboardCard
													card={card}
													isCustomizing={layoutSettings.isCustomizing}
													handleRemoveCard={handleRemoveCard}
													filter={globalFilter}
												/>
											</div>
										);
									})
								) : (
									<>No Card found!</>
								)}
							</ResponsiveGridLayout>
						)}
					</div>
				) : activeTab === 'Getting Started' ? (
					<GetStartedScreen setTotalPercentage={setTotalPercentage} />
				) : (
					<NotificationPage />
				)}
			</div>

			{isOpenModal && (
				<Modal
					className="deleteVendorsModal"
					title="Create New View"
					isOpen={isOpenModal}
					onClose={() => {
						setIsOpenModal(false);
					}}
					cancelButton={() => {
						setIsOpenModal(false);
					}}
				>
					<div
						style={{
							textAlign: 'start',
							padding: '10px 0px',
							fontWeight: '600',
							color: 'black',
						}}
					>
						<InputField
							label="Please enter the name of the new view"
							placeholder="View Name"
							type="text"
							autoComplete="off"
							required={false}
							autoFocus={false}
							value={cardName}
							onChange={(value) => {
								setCardName(value);
							}}
						/>
					</div>

					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '10px',
							justifyContent: 'start',
						}}
					>
						<Button
							type="button"
							button="Ok"
							className="mt-0 px-4 btn-primary relative"
							onClick={() => {
								handleCreateNewLayout();
							}}
						/>

						<Button
							type="button"
							button="Cancel"
							className="mt-0 px-4 btn-secondary relative"
							onClick={() => {
								setIsOpenModal(false);
							}}
						/>
					</div>
				</Modal>
			)}

			{isAddCardModal && (
				<AddCardModel
					isAddCardModal={isAddCardModal}
					setIsAddCardModal={setIsAddCardModal}
					layoutSettings={layoutSettings}
					setLayoutSettings={setLayoutSettings}
				/>
			)}

			{showSubscriptionModal && (
				<div
					className="modal fade show d-block"
					tabIndex="-1"
					aria-modal="true"
					role="dialog"
					style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
				>
					<div className="modal-dialog modal-dialog-top modal-xl">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">Subscription Required</h5>

								<button
									type="button"
									className="btn-close"
									onClick={() => setShowSubscriptionModal(false)}
								/>
							</div>

							<div className="modal-body w-100">
								{loading ? (
									<PageLoader />
								) : (
									<PricingSection
										billing={billing}
										setBilling={setBilling}
										plans={plans}
										hideFreePlan={true}
									/>
								)}
							</div>

							<div className="modal-footer">
								<div className="d-flex justify-content-end align-items-center gap-3 w-100">
									<button
										type="button"
										className="btn-cancel"
										onClick={() => setShowSubscriptionModal(false)}
									>
										Cancel
									</button>

									<button
										type="button"
										className="btn-primary-modal"
										onClick={() => {
											setShowSubscriptionModal(false);
											navigate('/subscription');
										}}
									>
										Subscribe Now
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

const DashboardCard = ({ card, isCustomizing, handleRemoveCard, filter }) => {
	const userDetails = useSelector((state) => state?.userInfo?.userInfo);
	if (!card) {
		return (
			<div className="h-full flex items-center justify-center bg-gray-50 rounded-xl border border-gray-200">
				<p className="text-gray-500">Card not found</p>
			</div>
		);
	}

	switch (card.type) {
		case 'summary':
			return (
				<SummaryCard
					key={card.layout_card_id}
					card={card}
					isCustomizing={isCustomizing}
					handleRemoveCard={handleRemoveCard}
					filter={filter}
				/>
			);
		case 'inventory_summary':
			if (
				!hasPermission('dashboard.manage_dashboard.view', userDetails?.data?.role?.role_permissions)
			)
				return (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: '100%',
							width: '100%',
						}}
					>
						<p>Locked</p>
					</div>
				);
			return (
				<InventorySummary
					key={card.layout_card_id}
					card={card}
					isCustomizing={isCustomizing}
					handleRemoveCard={handleRemoveCard}
					filter={filter}
				/>
			);
		case 'top_selling_products':
			if (
				!hasPermission('dashboard.manage_dashboard.view', userDetails?.data?.role?.role_permissions)
			)
				return (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: '100%',
							width: '100%',
						}}
					>
						Locked
					</div>
				);
			return (
				<SellingProducts
					key={card.layout_card_id}
					card={card}
					isCustomizing={isCustomizing}
					handleRemoveCard={handleRemoveCard}
					filter={filter}
				/>
			);
		case 'table':
			if (
				!hasPermission('dashboard.manage_dashboard.view', userDetails?.data?.role?.role_permissions)
			)
				return (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: '100%',
							width: '100%',
						}}
					>
						Locked
					</div>
				);
			return (
				<TableCard
					card={card}
					isCustomizing={isCustomizing}
					handleRemoveCard={handleRemoveCard}
					filter={filter}
				/>
			);
		case 'tabs':
			if (
				!hasPermission('dashboard.manage_dashboard.view', userDetails?.data?.role?.role_permissions)
			)
				return (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: '100%',
							width: '100%',
						}}
					>
						Locked
					</div>
				);
			return (
				<RecentTransactions
					card={card}
					isCustomizing={isCustomizing}
					handleRemoveCard={handleRemoveCard}
					filter={filter}
				/>
			);
		default:
			return (
				<div className="h-full flex items-center justify-center bg-gray-50 rounded-xl border border-gray-200">
					<p className="text-gray-500">Unsupported card type: {card.type}</p>
				</div>
			);
	}
};

export default Dashboard;
